import { LitElement, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhVideoCamera";
import "@phosphor-icons/webcomponents/PhPlus";
import "@phosphor-icons/webcomponents/PhPencil";
import "@phosphor-icons/webcomponents/PhTrash";
import "@phosphor-icons/webcomponents/PhCalendar";
import "@phosphor-icons/webcomponents/PhCircle";
import "@phosphor-icons/webcomponents/PhCheckCircle";
import "@phosphor-icons/webcomponents/PhXCircle";
import "@phosphor-icons/webcomponents/PhEye";
import "@phosphor-icons/webcomponents/PhShareNetwork";
import "@phosphor-icons/webcomponents/PhPause";
import "@phosphor-icons/webcomponents/PhStop";
import "@phosphor-icons/webcomponents/PhBroadcast";
import "ui-components/button/wow-button";
import "ui-components/go-live-button/go-live-button";
import "ui-components/status-badge/status-badge";
import "ui-components/modal/wow-modal";
import "ui-components/confirmation/wow-confirmation";
import "ui-components/image-or-video/image-or-video";

import { styles } from "./live-events-page.css";
import { appContext } from "ui-common/appContext";
import { featureFlagsQueryOptions } from "../../queries/feature-flags";
import { DataQueryController } from "ui-common/data-query-controller";
import { liveEventsQueryOptions } from "../../queries/live-events";
import { rpc } from "ui-common/services/rpc";
import { NotificationService } from "ui-components/notification";
import type { GetLiveEventsResult } from "api-contracts/rpc/get-live-events";

import "../../components/page-content/page-content";
import "../../components/view-primary-buttons/view-primary-buttons";
import "../../components/content-container/content-container";
import "../../components/create-live-event-modal/create-live-event-modal";
import "../../components/edit-live-event-modal/edit-live-event-modal";
import "../../components/share-content-modal/share-content-modal";
import "../../components/stream-config-modal/stream-config-modal";
import "../../components/live-stream-modal/live-stream-modal";
import type { ShareContentDetails } from "../../components/share-content-modal/share-content-modal";
import "../../components/live-event-preview-modal/live-event-preview-modal";
import type { LiveEventPreviewDetails } from "../../components/live-event-preview-modal/live-event-preview-modal";
import { getAssetUrl } from "helpers/assetHelpers";
import { t, translate } from "../../translations/i18n";

type LiveEventFilter = "all" | "upcoming" | "live" | "ended";

@customElement("live-events-page")
export class LiveEventsPage extends LitElement {
  static override styles = [styles];

  @state()
  private selectedFilter: LiveEventFilter = "all";

  @state()
  private showCreateModal = false;

  @state()
  private showEditModal = false;

  @state()
  private editingEventId?: string;

  @state()
  private deletingEventId?: string;

  @state()
  private stoppingEventId?: string;

  @state()
  private endingEventId?: string;

  @state()
  private showShareModal = false;

  @state()
  private shareContentDetails?: ShareContentDetails;

  @state()
  private showPreviewModal = false;

  @state()
  private previewEventDetails?: LiveEventPreviewDetails;

  @state()
  private showStreamConfigModal = false;

  @state()
  private showLiveStreamModal = false;

  @state()
  private streamingEventId?: string;

  @state()
  private streamConfig: { enableAudio: boolean } | null = null;

  private eventsQuery = new DataQueryController(this, () => {
    const status =
      this.selectedFilter === "all"
        ? undefined
        : this.selectedFilter === "upcoming"
          ? ("scheduled" as const)
          : (this.selectedFilter as "live" | "ended");
    return liveEventsQueryOptions(status);
  });

  private featureFlagsQuery = new DataQueryController(this, () => featureFlagsQueryOptions);

  private handleFilterChange(filter: LiveEventFilter) {
    this.selectedFilter = filter;
    this.eventsQuery.refetch();
  }

  private handleCreateClick() {
    this.editingEventId = undefined;
    this.showCreateModal = true;
  }

  private handleEditClick(event: GetLiveEventsResult["events"][0]) {
    this.editingEventId = event.id;
    this.showEditModal = true;
  }

  private handleDeleteClick(eventId: string) {
    this.deletingEventId = eventId;
  }

  private handleCancelDelete() {
    this.deletingEventId = undefined;
  }

  private async handleConfirmDelete() {
    if (!this.deletingEventId) return;

    try {
      await rpc("delete-live-event", { event_id: this.deletingEventId });
      NotificationService.success(translate("liveEventsPage.eventDeletedSuccess"));
      this.deletingEventId = undefined;
      this.eventsQuery.refetch();
    } catch (error) {
      console.error("Error deleting event:", error);
      NotificationService.error(translate("liveEventsPage.eventDeletedError"));
    }
  }

  private handleStopLiveClick(eventId: string) {
    this.stoppingEventId = eventId;
  }

  private handleCancelStopLive() {
    this.stoppingEventId = undefined;
  }

  private async handleConfirmStopLive() {
    if (!this.stoppingEventId) return;

    try {
      await rpc("stop-live-event", { event_id: this.stoppingEventId });
      NotificationService.success(translate("liveEventsPage.streamPausedSuccess"));
      this.stoppingEventId = undefined;
      this.eventsQuery.refetch();
    } catch (error) {
      console.error("Error stopping live event:", error);
      NotificationService.error(translate("liveEventsPage.streamPausedError"));
    }
  }

  private handleEndEventClick(eventId: string) {
    this.endingEventId = eventId;
  }

  private handleCancelEndEvent() {
    this.endingEventId = undefined;
  }

  private async handleConfirmEndEvent() {
    if (!this.endingEventId) return;

    try {
      await rpc("end-live-event", { event_id: this.endingEventId });
      NotificationService.success(translate("liveEventsPage.eventEndedSuccess"));
      this.endingEventId = undefined;
      this.eventsQuery.refetch();
    } catch (error) {
      console.error("Error ending event:", error);
      NotificationService.error(translate("liveEventsPage.eventEndedError"));
    }
  }

  private handleCreateModalClose() {
    this.showCreateModal = false;
  }

  private handleEditModalClose() {
    this.showEditModal = false;
    this.editingEventId = undefined;
  }

  private handleEventCreated(e: CustomEvent<{ event?: { id: string; title: string; thumbnail_url?: string | null } }>) {
    this.showCreateModal = false;
    this.eventsQuery.refetch();

    if (e.detail?.event) {
      this.shareContentDetails = this.buildShareContentDetails(
        e.detail.event.id,
        e.detail.event.title,
        e.detail.event.thumbnail_url,
      );
      this.showShareModal = true;
    }
  }

  private handleShareModalClose() {
    this.showShareModal = false;
    this.shareContentDetails = undefined;
  }

  private handleShareClick(event: GetLiveEventsResult["events"][0]) {
    this.previewEventDetails = this.buildPreviewEventDetails(event);
    this.showPreviewModal = true;
  }

  private handlePreviewModalClose() {
    this.showPreviewModal = false;
    this.previewEventDetails = undefined;
  }

  private handleShareWithAudience(e: CustomEvent<{ event: LiveEventPreviewDetails }>) {
    const event = e.detail.event;
    this.shareContentDetails = this.buildShareContentDetails(event.id, event.title, event.thumbnail_url);
    this.showPreviewModal = false;
    this.previewEventDetails = undefined;
    this.showShareModal = true;
  }

  private buildPreviewEventDetails(event: GetLiveEventsResult["events"][0]): LiveEventPreviewDetails {
    const isProduction = window.location.hostname.endsWith("worldofwow.com");
    const baseUrl = isProduction ? "https://worldofwow.com" : "https://worldofwow.dev";
    const handle = appContext.currentUser?.handle ?? "";
    return {
      id: event.id,
      title: event.title,
      description: event.description,
      thumbnail_url: event.thumbnail_url,
      scheduled_start_time: event.scheduled_start_time,
      status: event.status as LiveEventPreviewDetails["status"],
      shareUrl: `${baseUrl}/live-event/${handle}/${event.id}`,
      fee_cents: event.fee_cents,
      content_tier: event.content_tier,
      max_attendees: event.max_attendees,
    };
  }

  private buildShareContentDetails(id: string, title: string, thumbnail_url?: string | null): ShareContentDetails {
    const isProduction = window.location.hostname.endsWith("worldofwow.com");
    const baseUrl = isProduction ? "https://worldofwow.com" : "https://worldofwow.dev";
    const handle = appContext.currentUser?.handle ?? "";
    return {
      id,
      title,
      thumbnail_url,
      shareUrl: `${baseUrl}/live-event/${handle}/${id}`,
    };
  }

  private handleEventUpdated() {
    this.showEditModal = false;
    this.editingEventId = undefined;
    this.eventsQuery.refetch();
  }

  private handleEventEnded() {
    this.showEditModal = false;
    this.editingEventId = undefined;
    this.eventsQuery.refetch();
  }

  private handleStartStreamingClick(eventId: string) {
    this.streamingEventId = eventId;
    this.showStreamConfigModal = true;
  }

  private handleStreamConfigCancel() {
    this.showStreamConfigModal = false;
    this.streamingEventId = undefined;
  }

  private handleStartStream(e: CustomEvent) {
    this.streamConfig = e.detail;
    this.showStreamConfigModal = false;
    this.showLiveStreamModal = true;
  }

  private handleStreamEnded() {
    this.showLiveStreamModal = false;
    this.streamConfig = null;
    this.streamingEventId = undefined;
    this.eventsQuery.refetch();
  }

  private handleViewClick() {
    const handle = appContext.currentUser?.handle;
    if (handle) window.open(`/${handle}/live-events`, "_self", "noopener,noreferrer");
  }

  private handleEventWentLive() {
    this.eventsQuery.refetch();
  }

  private isStreamingEventLive(): boolean {
    if (!this.streamingEventId) return false;
    const events = this.eventsQuery.result?.data?.events || [];
    const event = events.find((e) => e.id === this.streamingEventId);
    return event?.status === "live";
  }

  private getComputedStatus(event: GetLiveEventsResult["events"][0]): string {
    return event.status;
  }

  private renderStatusBadge(event: GetLiveEventsResult["events"][0]) {
    const status = this.getComputedStatus(event);

    switch (status) {
      case "scheduled":
        return html`
          <status-badge variant="scheduled" size="medium">
            <ph-calendar slot="icon" size="14" weight="fill"></ph-calendar>
            ${t("liveEventsPage.scheduled")}
          </status-badge>
        `;
      case "live":
        return html`
          <status-badge variant="live" size="medium" pulse>
            <ph-circle slot="icon" size="14" weight="fill"></ph-circle>
            ${t("liveEventsPage.live")}
          </status-badge>
        `;
      case "ended":
        return html`
          <status-badge variant="ended" size="medium">
            <ph-check-circle slot="icon" size="14" weight="fill"></ph-check-circle>
            ${t("liveEventsPage.ended")}
          </status-badge>
        `;
      case "cancelled":
        return html`
          <status-badge variant="cancelled" size="medium">
            <ph-x-circle slot="icon" size="14" weight="fill"></ph-x-circle>
            ${t("liveEventsPage.cancelled")}
          </status-badge>
        `;
      default:
        return html`<status-badge size="medium">${status}</status-badge>`;
    }
  }

  private renderMainContent() {
    const featureFlags = this.featureFlagsQuery.result?.data?.features ?? [];
    const hasLiveEvents = featureFlags.includes("paid-live-events") || featureFlags.includes("open-live-events");

    // Show access denied message for non-enabled creators
    if (!hasLiveEvents) {
      return html` <div class="main-content">${this.renderEmptyState()}</div> `;
    }

    // Show loading state if query hasn't completed yet
    if (!this.eventsQuery.result || this.eventsQuery.result.isPending) {
      return html`
        <div class="main-content">
          <div class="loading-state">
            <div>${t("liveEventsPage.loadingEvents")}</div>
          </div>
        </div>
      `;
    }

    const events = this.eventsQuery.result.data?.events || [];

    return html`
      <div class="main-content">
        ${this.renderFilterButtons()} ${events.length > 0 ? this.renderEventsList(events) : this.renderEmptyState()}
      </div>
    `;
  }

  private renderEventsList(events: GetLiveEventsResult["events"]) {
    return html`
      <div class="events-list">
        ${events.map(
          (event) => html`
            <div class="event-card">
              ${event.thumbnail_url
                ? html`
                    <image-or-video
                      class="event-thumbnail"
                      url=${getAssetUrl(event.thumbnail_url)}
                      alt="${event.title}"
                    ></image-or-video>
                  `
                : nothing}
              <div class="event-content">
                <div class="event-header">
                  <h3>${event.title}</h3>
                  <div class="event-header-actions">
                    ${this.renderStatusBadge(event)}
                    <div class="event-actions">
                      <wow-button
                        variant="subtle"
                        size="small"
                        @click=${() => this.handleShareClick(event)}
                        aria-label="Share event"
                      >
                        <ph-share-network size="16"></ph-share-network>
                      </wow-button>
                      ${this.getComputedStatus(event) === "scheduled"
                        ? html`
                            <wow-button
                              variant="subtle"
                              size="small"
                              @click=${() => this.handleEditClick(event)}
                              aria-label="Edit event"
                            >
                              <ph-pencil size="16"></ph-pencil>
                            </wow-button>
                          `
                        : nothing}
                      ${this.getComputedStatus(event) === "ended"
                        ? html`
                            <wow-button
                              variant="subtle"
                              size="small"
                              @click=${() => this.handleEditClick(event)}
                              aria-label="View event"
                            >
                              <ph-eye size="16"></ph-eye>
                            </wow-button>
                          `
                        : nothing}
                      ${this.getComputedStatus(event) === "live"
                        ? html`
                            <wow-button
                              variant="subtle"
                              size="small"
                              @click=${() => this.handleEditClick(event)}
                              aria-label="Edit event"
                            >
                              <ph-pencil size="16"></ph-pencil>
                            </wow-button>
                          `
                        : nothing}
                      ${this.getComputedStatus(event) !== "live"
                        ? html`
                            <wow-button
                              variant="subtle"
                              size="small"
                              @click=${() => this.handleDeleteClick(event.id)}
                              aria-label="Delete event"
                            >
                              <ph-trash size="16"></ph-trash>
                            </wow-button>
                          `
                        : nothing}
                    </div>
                  </div>
                </div>
                ${event.description ? html`<p class="event-description">${event.description}</p>` : nothing}
                <div class="event-meta">
                  <div class="meta-item">
                    <strong>${t("liveEventsPage.scheduledLabel")}</strong> ${new Date(
                      event.scheduled_start_time,
                    ).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                  </div>
                  ${event.content_tier === "premium"
                    ? html`
                        <div class="meta-item">
                          <strong>${t("liveEventsPage.premiumEvent")}</strong>
                        </div>
                      `
                    : nothing}
                  ${event.max_attendees
                    ? html`
                        <div class="meta-item">
                          <strong>${t("liveEventsPage.maxAttendeesLabel")}</strong> ${event.max_attendees}
                        </div>
                      `
                    : nothing}
                  ${this.getComputedStatus(event) === "live"
                    ? html` <div class="streaming-buttons">
                        <wow-button
                          variant="neutral"
                          size="small"
                          @click=${() => this.handleStopLiveClick(event.id)}
                          aria-label="Pause live stream"
                        >
                          <ph-pause size="16" weight="fill"></ph-pause>
                          ${t("liveEventsPage.stopLive")}
                        </wow-button>
                        <wow-button
                          variant="danger"
                          size="small"
                          @click=${() => this.handleEndEventClick(event.id)}
                          aria-label="End event"
                        >
                          <ph-stop size="16" weight="fill"></ph-stop>
                          ${t("liveEventsPage.endEvent")}
                        </wow-button>
                      </div>`
                    : nothing}
                  ${this.getComputedStatus(event) === "scheduled"
                    ? html`
                        <div class="streaming-buttons">
                          <wow-button
                            variant="primary"
                            size="small"
                            @click=${() => this.handleStartStreamingClick(event.id)}
                            aria-label="Start streaming"
                          >
                            <ph-broadcast size="16" weight="fill"></ph-broadcast>
                            ${t("liveEventsPage.startStreaming")}
                          </wow-button>
                          <go-live-button
                            .eventId=${event.id}
                            variant="danger"
                            size="small"
                            .fullWidth=${false}
                            .translations=${{
                              goLive: t("liveEventsPage.goLiveWithObs"),
                              goingLive: t("liveEventsPage.goingLive"),
                              goLiveSuccess: t("liveEventsPage.goLiveSuccess"),
                              goLiveFailed: t("liveEventsPage.goLiveFailed"),
                              goLiveHeader: t("liveEventsPage.goLiveHeader"),
                              goLiveConfirm: t("liveEventsPage.goLiveConfirm"),
                              goLiveButton: t("liveEventsPage.goLiveButton"),
                              goLiveCancel: t("liveEventsPage.cancel"),
                            }}
                            @event-went-live=${this.handleEventWentLive}
                          ></go-live-button>
                        </div>
                      `
                    : nothing}
                </div>
              </div>
            </div>
          `,
        )}
      </div>
    `;
  }

  private renderFilterButtons() {
    return html`
      <div class="filter-buttons">
        <wow-button
          variant=${this.selectedFilter === "all" ? "primary" : "subtle"}
          size="small"
          @click=${() => this.handleFilterChange("all")}
        >
          ${t("liveEventsPage.all")}
        </wow-button>
        <wow-button
          variant=${this.selectedFilter === "upcoming" ? "primary" : "subtle"}
          size="small"
          @click=${() => this.handleFilterChange("upcoming")}
        >
          ${t("liveEventsPage.upcoming")}
        </wow-button>
        <wow-button
          variant=${this.selectedFilter === "live" ? "primary" : "subtle"}
          size="small"
          @click=${() => this.handleFilterChange("live")}
        >
          ${t("liveEventsPage.live")}
        </wow-button>
        <wow-button
          variant=${this.selectedFilter === "ended" ? "primary" : "subtle"}
          size="small"
          @click=${() => this.handleFilterChange("ended")}
        >
          ${t("liveEventsPage.ended")}
        </wow-button>
      </div>
    `;
  }

  private renderEmptyState() {
    const featureFlags = this.featureFlagsQuery.result?.data?.features ?? [];
    const hasLiveEvents = featureFlags.includes("paid-live-events") || featureFlags.includes("open-live-events");

    if (!hasLiveEvents) {
      return html`
        <div class="empty-state">
          <ph-video-camera size="64" weight="thin"></ph-video-camera>
          <h2>${t("liveEventsPage.notAvailableTitle")}</h2>
          <p>${t("liveEventsPage.notAvailableMessage")}</p>
        </div>
      `;
    }

    return html`
      <div class="empty-state">
        <ph-video-camera size="64" weight="thin"></ph-video-camera>
        <h2>${t("liveEventsPage.noLiveEventsYet")}</h2>
        <p>${t("liveEventsPage.createFirstLiveEvent")}</p>
        <wow-button variant="primary" @click=${this.handleCreateClick}>
          ${t("liveEventsPage.createLiveEvent")}
        </wow-button>
      </div>
    `;
  }

  override render() {
    return html`
      <page-content .pageTitle=${translate("liveEventsPage.pageTitle")}>
        <div slot="actions">
          <view-primary-buttons
            primaryText=${t("liveEventsPage.createLiveEvent")}
            @primary-click=${this.handleCreateClick}
            @view-click=${this.handleViewClick}
          ></view-primary-buttons>
        </div>

        <div class="containers">
          <content-container variant="primary" columns="1">
            <div slot="column" class="live-events-content">${this.renderMainContent()}</div>
          </content-container>
        </div>
      </page-content>

      ${this.showCreateModal
        ? html`
            <create-live-event-modal
              .featureFlags=${this.featureFlagsQuery.result?.data?.features ?? []}
              @modal-close=${this.handleCreateModalClose}
              @event-created=${this.handleEventCreated}
            ></create-live-event-modal>
          `
        : nothing}
      ${this.showEditModal && this.editingEventId
        ? html`
            <edit-live-event-modal
              .eventId=${this.editingEventId}
              .featureFlags=${this.featureFlagsQuery.result?.data?.features ?? []}
              @modal-close=${this.handleEditModalClose}
              @event-updated=${this.handleEventUpdated}
              @event-ended=${this.handleEventEnded}
            ></edit-live-event-modal>
          `
        : nothing}
      ${this.deletingEventId
        ? html`
            <wow-confirmation header=${translate("liveEventsPage.deleteEvent")}>
              <div class="delete-confirmation-content">
                <p>${t("liveEventsPage.deleteConfirmation")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmDelete}>
                    ${t("liveEventsPage.deleteEvent")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelDelete}>
                    ${t("liveEventsPage.cancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
      ${this.stoppingEventId
        ? html`
            <wow-confirmation header=${translate("liveEventsPage.pauseStreamHeader")}>
              <div class="delete-confirmation-content">
                <p>${t("liveEventsPage.pauseStreamMessage")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmStopLive}>
                    ${t("liveEventsPage.pauseStreamButton")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelStopLive}>
                    ${t("liveEventsPage.cancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
      ${this.endingEventId
        ? html`
            <wow-confirmation header=${translate("liveEventsPage.endEventHeader")}>
              <div class="delete-confirmation-content">
                <p>${t("liveEventsPage.endEventMessage")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmEndEvent}>
                    ${t("liveEventsPage.endEvent")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelEndEvent}>
                    ${t("liveEventsPage.cancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
      ${this.showPreviewModal && this.previewEventDetails
        ? html`
            <live-event-preview-modal
              .event=${this.previewEventDetails}
              @modal-close=${this.handlePreviewModalClose}
              @share-with-audience=${this.handleShareWithAudience}
            ></live-event-preview-modal>
          `
        : nothing}
      ${this.showShareModal && this.shareContentDetails
        ? html`
            <share-content-modal
              .content=${this.shareContentDetails}
              @modal-close=${this.handleShareModalClose}
            ></share-content-modal>
          `
        : nothing}
      ${this.showStreamConfigModal && this.streamingEventId
        ? html`
            <stream-config-modal
              .streamId=${this.streamingEventId}
              @start-stream=${this.handleStartStream}
              @cancel=${this.handleStreamConfigCancel}
            ></stream-config-modal>
          `
        : nothing}
      ${this.showLiveStreamModal && this.streamConfig && this.streamingEventId
        ? html`
            <live-stream-modal
              .streamId=${this.streamingEventId}
              ?enable-audio=${this.streamConfig.enableAudio}
              ?is-live=${this.isStreamingEventLive()}
              @stream-ended=${this.handleStreamEnded}
            ></live-stream-modal>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "live-events-page": LiveEventsPage;
  }
}
